import type { OnlineStatus } from '../game/session';
import { STAND_IN_NOTICE_MS } from '../net/roomClient';

/** A pane about a dropped connection (D-29), as the screen and the headset both show it. */
export interface ConnectionPane {
  /**
   * `waiting`: another player dropped and the AI takes over soon; `dropped`: this player did;
   * `reclaim`: the AI holds this player's seat until they say they are ready.
   */
  kind: 'waiting' | 'dropped' | 'reclaim';
  title: string;
  /** One line per player it is about. */
  text: string;
  /** The only button any of them has: OK, on the reclaim pane. */
  ok: boolean;
}

/** Whole seconds left of a countdown of `ms` that began at `since`, as the pane counts them. */
function secondsLeft(ms: number, since: number, now: number): number {
  return Math.max(0, Math.ceil((ms - (now - since)) / 1000));
}

/**
 * The pane an online game shows at `now` (`performance.now`), if any. It counts down on its own
 * from when each wait began; only the server decides when the AI takes a seat.
 */
export function connectionPane(
  status: OnlineStatus,
  now: number,
  finished = false,
): ConnectionPane | null {
  if (finished || status.ended !== null || status.connection === 'closed') return null;
  // Lost, and not caught up yet: until then nobody knows whose the seat is.
  if (status.lostAt !== null) {
    // A watcher has no seat to hand over; the banner says the connection is being restored.
    if (status.spectator) return null;
    const left = secondsLeft(STAND_IN_NOTICE_MS, status.lostAt, now);
    return {
      kind: 'dropped',
      title: 'Connection lost',
      text:
        left > 0
          ? `The AI takes over your seat in ${left}…`
          : 'The AI has taken over your seat. Reconnecting…',
      ok: false,
    };
  }
  if (status.standIn)
    return {
      kind: 'reclaim',
      title: 'Take back seat from AI',
      text: 'The AI has played your seat while you were away. Take it back when you are ready.',
      ok: true,
    };
  if (status.paused.length > 0)
    return {
      kind: 'waiting',
      title: 'Connection dropped',
      // One line per player who dropped, each with their own countdown.
      text: status.paused
        .map(({ name, secondsLeft: seconds, at }) => {
          const left = secondsLeft(seconds * 1000, at, now);
          return left > 0
            ? `${name} has lost the connection. The AI takes over in ${left}…`
            : `The AI is taking over for ${name}…`;
        })
        .join('\n'),
      ok: false,
    };
  return null;
}
